// =============================================================================
// inspections.ts — AA: inspection gates + AI prep-checklist read/generate.
// See workers/core-os/src/routes/inspections.ts.
//
//   list(projectId)              GET  the project's gates + cached checklist
//   generatePrep(projectId, id)  POST generate (Lane B) + cache one gate's
//                                checklist. Returns the ok-union; AI failures
//                                come back as { ok:false, error } at HTTP 200,
//                                so `request` does NOT throw on them — branch on
//                                the `ok` flag at the call site.
// =============================================================================

import { request } from './request'

export interface InspectionPrepChecklist {
  ready_on_site: string[]
  paperwork: string[]
  common_failures: string[]
}

export interface InspectionGate {
  id: string
  display_name: string
  gating_type: string
  inspection_authority: string | null
  outcome: string | null
  scheduled_at: string | null
  completed_at: string | null
  inspector_name: string | null
  correction_notes: string | null
  prep_checklist: InspectionPrepChecklist | null
  prep_checklist_generated_at: string | null
  prep_checklist_model: string | null
}

export interface InspectionListResponse {
  ok: true
  count: number
  gates: InspectionGate[]
}

export type GeneratePrepResult =
  | { ok: true; checklist: InspectionPrepChecklist; model: string }
  | { ok: false; error: string; detail: string }

export const inspectionsApi = {
  list: (projectId: string) =>
    request<InspectionListResponse>(
      `/api/projects/${encodeURIComponent(projectId)}/inspections`,
    ),
  generatePrep: (projectId: string, gateId: string) =>
    request<GeneratePrepResult>(
      `/api/projects/${encodeURIComponent(projectId)}/inspections/${encodeURIComponent(gateId)}/prep-checklist`,
      { method: 'POST' },
    ),
  // Record the AHJ result. `passed` marks the gate done; `failed` /
  // `correction_required` resurface it as the next inspection until it clears.
  recordOutcome: (
    projectId: string,
    gateId: string,
    body: {
      outcome: 'passed' | 'failed' | 'correction_required' | 'waived' | 'cancelled'
      inspector_name?: string
      notes?: string
      completed_at?: string
    },
  ) =>
    request<{ ok: true; gate: InspectionGate; request_id?: string }>(
      `/api/projects/${encodeURIComponent(projectId)}/inspections/${encodeURIComponent(gateId)}/outcome`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
}
